/**
 * Project Finance Tab - Main orchestrator component
 * 
 * Integrates with Phase 3 backend APIs:
 * - GET /api/finance/payments/project/{id}/summary
 * - POST /api/finance/invoices (create)
 * - POST /api/finance/invoices/{id}/post (post)
 * - POST /api/finance/payments (record payment)
 * - POST /api/finance/payments/{id}/void (void payment)
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

import { FinanceSummaryCards } from './FinanceSummaryCards';
import { InvoicesTable } from './InvoicesTable';
import { PaymentsTable } from './PaymentsTable';
import { CreateInvoiceDrawer } from './CreateInvoiceDrawer';
import { RecordPaymentDrawer } from './RecordPaymentDrawer';
import { canUserEdit } from './utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProjectFinanceTab = ({ project, user, onRefresh }) => {
  // Data state
  const [financeSummary, setFinanceSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Drawer states
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);

  // Permissions
  const canEdit = useMemo(() => canUserEdit(user?.role), [user?.role]);

  // Fetch project finance data
  const fetchFinanceData = useCallback(async (showRefresh = false) => {
    if (!project?.id) return;

    if (showRefresh) setRefreshing(true);

    try {
      // Fetch from Phase 3 endpoint
      const summaryRes = await axios.get(`${API}/finance/payments/project/${project.id}/summary`);
      const data = summaryRes.data;
      
      setFinanceSummary({
        total_invoiced: data.total_invoiced,
        total_paid: data.total_paid,
        total_balance_due: data.total_balance_due,
        invoice_count: data.invoice_count,
        payment_count: data.payment_count
      });
      
      setInvoices(data.invoices || []);
      setPayments(data.payments || []);
    } catch (error) {
      console.error('Error fetching finance data:', error);
      // Fallback: Try legacy accounting endpoint
      try {
        const legacyRes = await axios.get(`${API}/jobs/${project.id}/accounting`);
        const legacyData = legacyRes.data;
        
        setFinanceSummary({
          total_invoiced: legacyData.total_invoiced || 0,
          total_paid: legacyData.total_collected || 0,
          total_balance_due: legacyData.ar_balance || 0,
          invoice_count: legacyData.invoices?.length || 0,
          payment_count: legacyData.payments?.length || 0
        });
        
        setInvoices(legacyData.invoices || []);
        setPayments(legacyData.payments || []);
      } catch (legacyError) {
        console.error('Legacy fallback also failed:', legacyError);
        toast.error('Failed to load finance data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [project?.id]);

  // Initial load
  useEffect(() => {
    fetchFinanceData();
  }, [fetchFinanceData]);

  // Handle create invoice
  const handleCreateInvoice = async (data) => {
    try {
      await axios.post(`${API}/finance/invoices`, data);
      toast.success('Invoice created successfully');
      await fetchFinanceData(true);
      onRefresh?.();
    } catch (error) {
      console.error('Create invoice error:', error);
      throw error;
    }
  };

  // Handle post invoice
  const handlePostInvoice = async (invoice) => {
    if (!window.confirm(`Post invoice ${invoice.transaction_number || invoice.invoice_number}? This will create a journal entry.`)) {
      return;
    }

    try {
      await axios.post(`${API}/finance/invoices/${invoice.id}/post`);
      toast.success('Invoice posted to ledger');
      await fetchFinanceData(true);
      onRefresh?.();
    } catch (error) {
      console.error('Post invoice error:', error);
      toast.error(error.response?.data?.detail || 'Failed to post invoice');
    }
  };

  // Handle record payment
  const handleRecordPayment = async (data) => {
    try {
      await axios.post(`${API}/finance/payments`, data);
      toast.success('Payment recorded successfully');
      await fetchFinanceData(true);
      onRefresh?.();
    } catch (error) {
      console.error('Record payment error:', error);
      throw error;
    }
  };

  // Handle void payment
  const handleVoidPayment = async (payment) => {
    const reason = window.prompt(`Void payment ${payment.transaction_number}?\n\nEnter reason:`);
    if (!reason) return;

    try {
      await axios.post(`${API}/finance/payments/${payment.id}/void`, { reason });
      toast.success('Payment voided with reversal');
      await fetchFinanceData(true);
      onRefresh?.();
    } catch (error) {
      console.error('Void payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to void payment');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="finance-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // No project
  if (!project) {
    return (
      <div className="text-center py-12 text-slate-500">
        No project selected
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-finance-tab">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Project Finance</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchFinanceData(true)}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <FinanceSummaryCards
        financeSummary={financeSummary}
        contractValue={project.contract_value || project.total_contract_price || 0}
      />

      {/* Invoices Table */}
      <InvoicesTable
        invoices={invoices}
        canEdit={canEdit}
        onCreateInvoice={() => setCreateInvoiceOpen(true)}
        onPostInvoice={handlePostInvoice}
        onViewInvoice={(inv) => console.log('View invoice:', inv)}
      />

      {/* Payments Table */}
      <PaymentsTable
        payments={payments}
        canEdit={canEdit}
        onRecordPayment={() => setRecordPaymentOpen(true)}
        onVoidPayment={handleVoidPayment}
        onViewPayment={(pmt) => console.log('View payment:', pmt)}
      />

      {/* Drawers */}
      <CreateInvoiceDrawer
        isOpen={createInvoiceOpen}
        onClose={() => setCreateInvoiceOpen(false)}
        onSubmit={handleCreateInvoice}
        project={project}
        contact={{ id: project.contact_id, name: project.contact_name }}
      />

      <RecordPaymentDrawer
        isOpen={recordPaymentOpen}
        onClose={() => setRecordPaymentOpen(false)}
        onSubmit={handleRecordPayment}
        invoices={invoices}
        project={project}
      />
    </div>
  );
};

export default ProjectFinanceTab;
